import { Button, notify } from '@affine/component';
import type { GoogleConnectFailure } from '@affine/core/components/hooks/use-google-connect';
import {
  useGoogleConnectionStatus,
  useGoogleConnectPopup,
} from '@affine/core/components/hooks/use-google-connect';
import { FetchService } from '@affine/core/modules/cloud';
import { GoogleIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import { IntegrationSettingHeader } from '../setting';
import * as styles from './setting-panel.css';

// Only show the spinner once the user has been waiting a moment, so a fast
// round-trip doesn't produce a flash of loading UI.
const SPINNER_DELAY_MS = 1000;

const GoogleSettingHeader = ({ action }: { action?: React.ReactNode }) => {
  return (
    <IntegrationSettingHeader
      icon={<GoogleIcon />}
      name="Google"
      desc="Send documents by email through Gmail"
      action={action}
    />
  );
};

export const GoogleSettingPanel = () => {
  const fetchService = useService(FetchService);

  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectSpinner, setShowDisconnectSpinner] = useState(false);

  const handleStatusError = useCallback((error: unknown) => {
    notify.error({
      title: 'Failed to load Google connection status',
      message:
        error instanceof Error
          ? error.name === 'NETWORK_ERROR'
            ? 'Please check your network connection and try again'
            : error.message
          : 'Please try again',
    });
  }, []);

  const { status, refresh: refreshStatus } = useGoogleConnectionStatus({
    onError: handleStatusError,
  });

  const handleConnected = useCallback(() => {
    notify.success({ title: 'Connected to Google' });
    refreshStatus().catch(console.error);
  }, [refreshStatus]);

  const handleConnectFailed = useCallback(
    (failure: GoogleConnectFailure) => {
      notify.error({
        title: 'Failed to connect',
        message:
          failure.reason === 'timeout'
            ? 'The connection window took too long to respond. If it is still open, please close it and try again.'
            : failure.reason === 'popup-error'
              ? failure.error instanceof Error
                ? failure.error.message
                : 'Please try again'
              : 'Please try again',
      });
      if (failure.reason === 'popup-error') return;
      // The popup may have failed silently (e.g. an expired nonce that never
      // posts back), so re-check status in case it actually did complete.
      // Skipped when the popup never opened at all — nothing can have changed.
      refreshStatus().catch(console.error);
    },
    [refreshStatus]
  );

  const {
    connect: handleConnect,
    connecting,
    showSpinner: showConnectSpinner,
  } = useGoogleConnectPopup({
    onConnected: handleConnected,
    onFailed: handleConnectFailed,
  });

  const handleDisconnect = useCallback(() => {
    setDisconnecting(true);
    const spinnerTimer = setTimeout(
      () => setShowDisconnectSpinner(true),
      SPINNER_DELAY_MS
    );

    fetchService
      .fetch('/api/google/disconnect', { method: 'POST' })
      .then(() => {
        notify.success({ title: 'Disconnected from Google' });
        return refreshStatus();
      })
      .catch((error: unknown) => {
        notify.error({
          title: 'Failed to disconnect',
          message: error instanceof Error ? error.message : 'Please try again',
        });
      })
      .finally(() => {
        clearTimeout(spinnerTimer);
        setDisconnecting(false);
        setShowDisconnectSpinner(false);
      });
  }, [fetchService, refreshStatus]);

  // `'error'` (the status call itself failed) deliberately falls through to
  // the not-connected view at the bottom, exactly as before the hook was
  // extracted: the user has already been notified, and offering Connect is
  // the only useful thing this panel can do without a status.
  if (status === 'loading') {
    return <GoogleSettingHeader />;
  }

  if (status === 'connected') {
    return (
      <div>
        <GoogleSettingHeader
          action={
            <Button
              variant="error"
              onClick={handleDisconnect}
              disabled={disconnecting}
              loading={showDisconnectSpinner}
            >
              Disconnect
            </Button>
          }
        />
        <div className={styles.connectedSection}>
          <div className={styles.connectedMessage}>
            Your Google account is connected. Documents can be sent by email
            through Gmail.
          </div>
        </div>
      </div>
    );
  }

  if (status === 'reconnect-required') {
    return (
      <div>
        <GoogleSettingHeader
          action={
            <Button
              variant="primary"
              onClick={handleConnect}
              disabled={connecting}
              loading={showConnectSpinner}
            >
              Reconnect
            </Button>
          }
        />
        <div className={styles.connectSection}>
          <div className={styles.reconnectMessage}>
            Your Google connection expired or was revoked. Reconnect to continue
            sending documents by email through Gmail.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <GoogleSettingHeader />
      <div className={styles.connectSection}>
        <div className={styles.hint}>
          Connect your Google account to send documents by email through Gmail.
        </div>
        <Button
          variant="primary"
          size="large"
          onClick={handleConnect}
          disabled={connecting}
          loading={showConnectSpinner}
          className={styles.connectButton}
          prefix={<GoogleIcon />}
        >
          Connect
        </Button>
      </div>
    </div>
  );
};
